import { useState, useEffect, useCallback } from "react"
import { Helmet } from "react-helmet-async"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Progress } from "@/components/ui/progress"
import { Navigation } from "@/components/Navigation"
import { Link } from "react-router-dom"
import { getScanTypeDisplayName } from "@/utils/displayNames"
import { fetchWithRefresh } from "@/utils/fetchWithRefresh"
import { useReportActions } from "@/hooks/useReportActions"
import { ReportPreview } from "@/components/reports/ReportPreview"
import { ToastContainer } from "@/components/ui/toast"
import { useToast } from "@/hooks/use-toast"
import { RefreshCw, Loader2, Zap, ArrowRight } from "lucide-react"
import { ScanProgressTracker } from "@/components/ScanProgressTracker"
import { useAuth } from "@/contexts/AuthContext"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"

interface Scan {
  id: string
  target_url?: string
  url?: string
  scan_type: string
  status: string
  vulnerabilities_count?: number
  security_score?: number
  created_at: string
  updated_at: string
  progress?: number
  time_elapsed?: number
  queue_position?: number
  estimated_wait_minutes?: number
}

interface DashboardStats {
  totalScans: number
  activeScans: number
  totalVulnerabilities: number
  averageSecurityScore: number
}

export default function Dashboard() {
  const { user, checkAuthStatus } = useAuth()
  const { toasts, removeToast, toast } = useToast()
  const [stats, setStats] = useState<DashboardStats>({
    totalScans: 0,
    activeScans: 0,
    totalVulnerabilities: 0,
    averageSecurityScore: 0
  })
  const [recentScans, setRecentScans] = useState<Scan[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Track active scans for polling
  const [activeScans, setActiveScans] = useState<Set<string>>(new Set())

  // Use shared report actions hook
  const { selectedReport, reportData, handleViewReport, handleDownloadReport, handleClosePreview } = useReportActions(toast)

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true)

      // Fetch recent scans (limit to 5 for dashboard)
      const scansResponse = await fetchWithRefresh('/api/scan/list?per_page=5&page=1', {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        }
      })

      if (scansResponse.ok) {
        const scansData = await scansResponse.json()
        setRecentScans(scansData.scans || [])

        // Calculate vulnerabilities from recent scans only (to avoid misleading aggregation)
        const totalVulns = scansData.scans?.reduce((total: number, scan: Scan) => total + (scan.vulnerabilities_count || 0), 0) || 0

        // Fetch actual totals for completed and active scans
        const [completedResponse, runningResponse, queuedResponse] = await Promise.all([
          fetchWithRefresh('/api/scan/list?status=completed&per_page=1', {
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' }
          }),
          fetchWithRefresh('/api/scan/list?status=running&per_page=1', {
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' }
          }),
          fetchWithRefresh('/api/scan/list?status=queued&per_page=1', {
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' }
          })
        ])

        let totalCompletedScans = 0
        let totalActiveScans = 0

        if (completedResponse.ok) {
          const completedData = await completedResponse.json()
          totalCompletedScans = completedData.total || 0
        }

        if (runningResponse.ok) {
          const runningData = await runningResponse.json()
          totalActiveScans += runningData.total || 0
        }

        if (queuedResponse.ok) {
          const queuedData = await queuedResponse.json()
          totalActiveScans += queuedData.total || 0
        }

        setStats({
          totalScans: totalCompletedScans,
          activeScans: totalActiveScans,
          totalVulnerabilities: totalVulns,
          averageSecurityScore: 0 // Will be calculated when we have vulnerability data
        })
      } else {
        // Handle auth errors gracefully
        if (scansResponse.status === 401) {
          setError('Please log in to view your dashboard')
        } else {
          setError('Failed to load dashboard data')
        }
      }
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err)
      setError('Failed to connect to server')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchDashboardData()
  }, [fetchDashboardData])

  const getStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case "completed":
        return <Badge variant="default">Completed</Badge>
      case "partially_completed":
        return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-400">Partially Completed</Badge>
      case "running":
        return <Badge variant="secondary">Running</Badge>
      case "queued":
        return <Badge variant="outline">Queued</Badge>
      case "failed":
        return <Badge variant="destructive">Failed</Badge>
      default:
        return <Badge variant="outline">{status}</Badge>
    }
  }

  const getScoreBadge = (score: number | null | undefined, status: string) => {
    // For running or queued scans, show pending indicator
    if (status === 'running' || status === 'queued') {
      return <Badge className="bg-gray-100 text-gray-800">-/100</Badge>
    }

    // For completed and partially_completed scans, show actual score
    const finalScore = score || 0
    if (finalScore >= 90) return <Badge className="bg-green-100 text-green-800">{finalScore}/100</Badge>
    if (finalScore >= 70) return <Badge className="bg-yellow-100 text-yellow-800">{finalScore}/100</Badge>
    return <Badge className="bg-red-100 text-red-800">{finalScore}/100</Badge>
  }

  const getVulnBadge = (count: number) => {
    if (count === 0) return <Badge variant="default">Clean</Badge>
    if (count <= 5) return <Badge variant="secondary">{count} Issues</Badge>
    return <Badge variant="destructive">{count} Issues</Badge>
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A'

    try {
      const date = new Date(dateString)

      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'Invalid Date'
      }

      // Format as: "Sep 14, 2025 at 10:30 PM"
      const dateStr = date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
      const timeStr = date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      })
      return `${dateStr} at ${timeStr}`
    } catch (error) {
      console.error('Error formatting date:', error)
      return 'Invalid Date'
    }
  }

  const formatUrlForDisplay = (url: string, scanType: string) => {
    // NMAP scans strip https:// for functionality, but we want to display it
    if (scanType.startsWith('nmap_') && !url.startsWith('http://') && !url.startsWith('https://')) {
      return `https://${url}`
    }
    return url
  }

  const handleRefresh = () => {
    fetchDashboardData()
  }

  // Update scan progress in state
  const updateScanProgress = useCallback((scanId: string, progress: number, status: string, security_score?: number, vulnerabilities_count?: number, queue_position?: number, estimated_wait_minutes?: number) => {
    setRecentScans(prevScans =>
      prevScans.map(scan => {
        if (scan.id === scanId) {
          // If status changed to completed or failed, mark progress accordingly
          if (status === 'completed') {
            return {
              ...scan,
              progress: 100,
              status: 'completed',
              security_score: security_score ?? scan.security_score,
              vulnerabilities_count: vulnerabilities_count ?? scan.vulnerabilities_count
            }
          } else if (status === 'failed') {
            return {
              ...scan,
              progress: 0,
              status: 'failed',
              security_score: security_score ?? scan.security_score,
              vulnerabilities_count: vulnerabilities_count ?? scan.vulnerabilities_count
            }
          }
          return {
            ...scan,
            progress,
            status,
            security_score: security_score ?? scan.security_score,
            vulnerabilities_count: vulnerabilities_count ?? scan.vulnerabilities_count,
            queue_position,
            estimated_wait_minutes
          }
        }
        return scan
      })
    )
  }, [])

  // Handle scan completion
  const handleScanComplete = useCallback((scanId: string) => {
    // Remove from active scans first to prevent duplicate handlers
    setActiveScans(prev => {
      const newSet = new Set(prev)
      const wasActive = newSet.has(scanId)
      newSet.delete(scanId)

      // Only show toast and refresh if this scan was actually being tracked
      if (wasActive) {
        // Refresh dashboard to get final results
        fetchDashboardData()

        // Refresh user data to ensure scan counter is up-to-date
        checkAuthStatus()

        // Show completion toast (only once)
        toast({
          title: "Scan completed",
          description: "Your security scan has finished processing.",
        })
      }

      return newSet
    })
  }, [fetchDashboardData, checkAuthStatus, toast])

  // Handle scan error (e.g., 404 when scan is deleted)
  const handleScanError = useCallback((scanId: string, error: string) => {
    // Remove from active scans immediately
    setActiveScans(prev => {
      const newSet = new Set(prev)
      newSet.delete(scanId)
      return newSet
    })

    // Remove from scans list immediately (for instant UI update)
    setRecentScans(prevScans => prevScans.filter(scan => scan.id !== scanId))

    // Refresh dashboard data to update scan limits and stats
    fetchDashboardData()
  }, [fetchDashboardData])

  // Track active scans
  useEffect(() => {
    const activeScanIds = recentScans
      .filter(scan => scan.status === 'running' || scan.status === 'queued')
      .map(scan => scan.id)

    setActiveScans(new Set(activeScanIds))
  }, [recentScans])

  if (error) {
    return (
      <div className="flex min-h-screen w-full flex-col">
        <Navigation />
        <main className="flex flex-1 flex-col gap-4 p-4 md:gap-8 md:p-8">
          <div className="flex items-center justify-center h-64">
            <Card className="w-full max-w-md">
              <CardHeader>
                <CardTitle className="text-center text-red-600">Access Error</CardTitle>
              </CardHeader>
              <CardContent className="text-center space-y-4">
                <p className="text-muted-foreground">{error}</p>
                <Link to="/login">
                  <Button>Go to Login</Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen w-full flex-col">
      <Helmet>
        <title>Dashboard | Fusegu Security</title>
        <meta name="description" content="View your security scan dashboard, recent scans, and vulnerability reports." />
      </Helmet>
      <Navigation />
      <ToastContainer toasts={toasts} onDismiss={removeToast} />

      <main className="flex flex-1 flex-col gap-4 px-4 pb-4 md:gap-8 md:px-8 md:pb-8 pt-20">
        {/* Upgrade Banner for Trial and None Tier Users */}
        {(user?.subscription_tier === 'trial' || user?.subscription_tier === 'none') && (
          <Card className="border-l-4 border-l-blue-500 bg-gradient-to-r from-blue-100 to-indigo-100 dark:from-blue-900/20 dark:to-indigo-900/20 shadow-sm">
            <CardContent className="py-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-500 rounded-lg flex-shrink-0">
                    <Zap className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      Upgrade to Pro for 50 scans per day
                    </p>
                    <p className="text-xs text-gray-700 dark:text-gray-300">
                      Unlimited time and priority support included
                    </p>
                  </div>
                </div>
                <Link to="/upgrade" className="w-full sm:w-auto">
                  <Button
                    size="sm"
                    className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white font-medium shadow-md hover:shadow-lg transition-all"
                  >
                    Upgrade Now
                    <ArrowRight className="ml-2 h-3 w-3" />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats Cards */}
        <div className="grid gap-4 md:grid-cols-2 md:gap-8 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Total Scans
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {loading ? "..." : stats.totalScans.toLocaleString()}
              </div>
              <p className="text-xs text-muted-foreground">
                Security assessments completed
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Active Scans
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {loading ? "..." : stats.activeScans}
              </div>
              <p className="text-xs text-muted-foreground">
                Currently running
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Issues Found
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {loading ? "..." : stats.totalVulnerabilities.toLocaleString()}
              </div>
              <p className="text-xs text-muted-foreground">
                From recent scans
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Quick Actions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Link to="/scans">
                <Button size="sm" className="w-full">
                  New Scan
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
        
        {/* Recent Scans Section */}
        <div className="grid gap-4 md:gap-8">
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <CardTitle>Recent Security Scans</CardTitle>
                  <CardDescription>
                    Your latest vulnerability assessments
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Link to="/scans" className="flex-1 sm:flex-none">
                    <Button variant="outline" size="sm" className="w-full sm:w-auto">
                      Start New Scan
                    </Button>
                  </Link>
                  <Button
                    onClick={handleRefresh}
                    variant="outline"
                    size="sm"
                    disabled={loading}
                  >
                    <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="text-sm text-muted-foreground">Loading your scan history...</div>
                </div>
              ) : recentScans.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-gray-500 mb-4">
                    <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No scans yet</h3>
                  <p className="text-gray-500 mb-4">
                    Start your first security assessment to identify vulnerabilities
                  </p>
                  <Link to="/scans">
                    <Button>Start Your First Scan</Button>
                  </Link>
                </div>
              ) : (
                <>
                  {/* Mobile: Card view, Desktop: Table view */}
                  <div className="md:hidden space-y-4">
                    {recentScans.map((scan) => {
                      const progress = scan.progress ?? 0
                      const isActive = scan.status === 'running' || scan.status === 'queued'

                      return (
                        <Card key={scan.id} className="p-4">
                          <div className="space-y-3">
                            {/* Target URL */}
                            <div>
                              <p className="text-xs text-muted-foreground mb-1">Target</p>
                              <p className="text-sm font-medium break-all">
                                {formatUrlForDisplay(scan.target_url || scan.url || 'N/A', scan.scan_type)}
                              </p>
                            </div>

                            {/* Scan Type & Status */}
                            <div className="flex gap-4">
                              <div className="flex-1">
                                <p className="text-xs text-muted-foreground mb-1">Type</p>
                                <Badge variant="outline" className="text-xs">
                                  {getScanTypeDisplayName(scan.scan_type || 'unknown')}
                                </Badge>
                              </div>
                              <div className="flex-1">
                                <p className="text-xs text-muted-foreground mb-1">Status</p>
                                <div className="flex items-center gap-2">
                                  {isActive && <Loader2 className="h-3 w-3 animate-spin" />}
                                  {getStatusBadge(scan.status)}
                                </div>
                              </div>
                            </div>

                            {/* Progress */}
                            {(scan.status === 'running' || scan.status === 'queued') && (
                              <div>
                                <p className="text-xs text-muted-foreground mb-1">Progress</p>
                                {scan.status === 'queued' && scan.queue_position !== undefined ? (
                                  <div className="space-y-1">
                                    <p className="text-sm">Position: {scan.queue_position + 1}</p>
                                    {scan.estimated_wait_minutes !== undefined && (
                                      <p className="text-xs text-muted-foreground">
                                        {scan.estimated_wait_minutes > 0
                                          ? `~${scan.estimated_wait_minutes} min wait`
                                          : 'Starting soon'}
                                      </p>
                                    )}
                                  </div>
                                ) : (
                                  <div className="space-y-1">
                                    <Progress value={progress} className="h-2" />
                                    <p className="text-xs text-muted-foreground">{progress}%</p>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Score & Issues */}
                            <div className="flex gap-4">
                              <div className="flex-1">
                                <p className="text-xs text-muted-foreground mb-1">Score</p>
                                {getScoreBadge(scan.security_score, scan.status)}
                              </div>
                              <div className="flex-1">
                                <p className="text-xs text-muted-foreground mb-1">Issues</p>
                                {getVulnBadge(scan.vulnerabilities_count || 0)}
                              </div>
                            </div>

                            {/* Date */}
                            <div>
                              <p className="text-xs text-muted-foreground mb-1">Generated</p>
                              <p className="text-xs">{formatDate(scan.created_at)}</p>
                            </div>

                            {/* Actions */}
                            <div className="flex gap-2 pt-2 border-t">
                              <Button
                                variant="outline"
                                size="sm"
                                className="flex-1"
                                onClick={() => handleViewReport(scan.id)}
                                disabled={scan.status !== "completed" && scan.status !== "partially_completed"}
                              >
                                View
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="flex-1"
                                onClick={() => handleDownloadReport(scan.id)}
                                disabled={scan.status !== "completed" && scan.status !== "partially_completed"}
                              >
                                Download
                              </Button>
                            </div>
                          </div>
                        </Card>
                      )
                    })}
                  </div>

                  {/* Desktop Table View */}
                  <div className="hidden md:block overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Target</TableHead>
                          <TableHead>Scan Type</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Progress</TableHead>
                          <TableHead>Security Score</TableHead>
                          <TableHead>Issues</TableHead>
                          <TableHead>Generated</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                    <TableBody>
                      {recentScans.map((scan) => {
                        const progress = scan.progress ?? 0
                        const isActive = scan.status === 'running' || scan.status === 'queued'

                        return (
                          <TableRow key={scan.id}>
                            <TableCell className="font-medium">
                              {formatUrlForDisplay(scan.target_url || scan.url || 'N/A', scan.scan_type)}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">{getScanTypeDisplayName(scan.scan_type || 'unknown')}</Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {isActive && <Loader2 className="h-3 w-3 animate-spin" />}
                                {getStatusBadge(scan.status)}
                              </div>
                            </TableCell>
                            <TableCell>
                              {scan.status === 'queued' && scan.queue_position !== undefined ? (
                                <div className="space-y-1">
                                  <p className="text-sm text-muted-foreground">
                                    Position: {scan.queue_position + 1}
                                  </p>
                                  {scan.estimated_wait_minutes !== undefined && (
                                    <p className="text-xs text-muted-foreground">
                                      {scan.estimated_wait_minutes > 0
                                        ? `~${scan.estimated_wait_minutes} min wait`
                                        : 'Starting soon'}
                                    </p>
                                  )}
                                </div>
                              ) : isActive ? (
                                <div className="space-y-1">
                                  <Progress value={progress} className="h-2 w-24" />
                                  <p className="text-xs text-muted-foreground">{progress}%</p>
                                </div>
                              ) : scan.status === 'completed' || scan.status === 'partially_completed' ? (
                                <span className="text-sm text-muted-foreground">Complete</span>
                              ) : scan.status === 'failed' ? (
                                <span className="text-sm text-destructive">Failed</span>
                              ) : (
                                <span className="text-sm text-muted-foreground">-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {getScoreBadge(scan.security_score, scan.status)}
                            </TableCell>
                            <TableCell>
                              {getVulnBadge(scan.vulnerabilities_count || 0)}
                            </TableCell>
                            <TableCell className="text-sm text-gray-600">
                              {formatDate(scan.created_at)}
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleViewReport(scan.id)}
                                  disabled={scan.status !== "completed" && scan.status !== "partially_completed"}
                                >
                                  View
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDownloadReport(scan.id)}
                                  disabled={scan.status !== "completed" && scan.status !== "partially_completed"}
                                >
                                  Download
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                  </div>

                  <div className="mt-4 text-center">
                    <Link to="/reports">
                      <Button variant="outline">
                        View All Reports
                      </Button>
                    </Link>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {selectedReport && reportData && (
        <ReportPreview
          reportData={reportData}
          onClose={handleClosePreview}
          onDownload={() => handleDownloadReport(selectedReport)}
        />
      )}

      {/* Scan Progress Trackers - invisible components that poll active scans */}
      {Array.from(activeScans).map(scanId => (
        <ScanProgressTracker
          key={scanId}
          scanId={scanId}
          onUpdate={updateScanProgress}
          onComplete={handleScanComplete}
          onError={handleScanError}
          toast={toast}
        />
      ))}
    </div>
  )
}